import { createRouter, createWebHistory } from 'vue-router';
import { UserManager, WebStorageStateStore } from 'oidc-client';
import { useAuthStore } from '@/store/auth';
import { usePermissionsStore } from '@/store/permissions';
import { useSnackbarStore } from '@/store/snackbar';
import i18n from '@/plugins/i18n';

// Operators only get WIP:R (not PROD:R, which every broader role keeps) -
// landing them on /dashboard would 403 on its component/order widgets, so
// route them straight to the one screen their permission set can actually
// use. Used everywhere the app currently hard-redirects to '/dashboard'.
const getDefaultLandingRoute = (permissionsStore) => {
  if (permissionsStore.can('PROD:R')) return '/dashboard';
  if (permissionsStore.can('WIP:R')) return '/operator-panel';
  return '/dashboard';
};

// Opaque per-browser id, persisted in localStorage - sent as an extra
// /connect/authorize param so identity-service can scope "revoke this
// client's prior refresh token on new login" to this device alone, instead
// of wiping every other device signed into avn-web (see DeviceId on
// AuthorizeRequest/AuthCode in iprod-identity-service).
const getOrCreateDeviceId = () => {
  const STORAGE_KEY = 'iprod_device_id';
  try {
    let id = window.localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private browsing, blocked storage) - fall
    // back to a per-load id rather than throwing; worst case this device
    // just doesn't get precise revocation scoping this session.
    return crypto.randomUUID();
  }
};

const userManager = new UserManager({
  authority: 'https://www.iprod.com.ua/identityservice/',
  client_id: 'avn-web',
  post_logout_redirect_uri: window.location.origin,
  redirect_uri: window.location.origin + '/callback',
  response_type: 'code',
  scope: 'openid profile org offline_access',
  extraQueryParams: { device_id: getOrCreateDeviceId() },
  userStore: new WebStorageStateStore({
    store: window.localStorage
  })
});

const routes = [
  // Public routes (non-authenticated)
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    meta: { public: true },
    children: [
      {
        path: '',
        name: 'landing',
        component: () => import('@/views/Landing.vue')
      },
      {
        path: 'game',
        name: 'game',
        component: () => import('@/views/Game.vue')
      }
    ]
  },

  // Auth callback
  {
    path: '/callback',
    name: 'callback',
    component: {
      template:
        '<div class="d-flex justify-center align-center" style="height: 100vh;"><v-progress-circular indeterminate color="primary" size="64" /></div>'
    },
    meta: { public: true },
    beforeEnter: async (to, from, next) => {
      try {
        const user = await userManager.signinRedirectCallback();
        const authStore = useAuthStore();
        authStore.setUser(user);
        const permissionsStore = usePermissionsStore();
        await permissionsStore.fetch();
        next(getDefaultLandingRoute(permissionsStore));
      } catch (error) {
        console.error('Callback error:', error);
        next('/');
      }
    }
  },

  // Work order traveler - printable, deliberately has no parent layout (no
  // sidebar/header chrome) so the page is just the paper document itself.
  // Still requires auth/permission like any other production-data route.
  {
    path: '/travelers/:wipItemId',
    name: 'wip-traveler',
    component: () => import('@/views/WipTraveler.vue'),
    meta: { requiresAuth: true, requiredPermission: 'WIP:R' }
  },

  // Production Specification report - same bare/chrome-less shape as the
  // traveler above, route-level (not tied to any order/WIP instance).
  {
    path: '/routes/:routeId/specification',
    name: 'route-specification',
    component: () => import('@/views/RouteSpecification.vue'),
    meta: { requiresAuth: true, requiredPermission: 'ROUTE:R' }
  },

  // Protected routes (authenticated)
  {
    path: '/dashboard',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/Dashboard.vue')
      }
    ]
  },
  {
    path: '/activity-log',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'activity-log',
        component: () => import('@/views/ActivityLog.vue')
      }
    ]
  },
  {
    path: '/components',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'COMP:R' },
    children: [
      {
        path: '',
        name: 'components',
        component: () => import('@/views/Components.vue')
      },
      {
        path: ':id',
        name: 'component-details',
        component: () => import('@/views/ComponentDetails.vue')
      }
    ]
  },
  {
    path: '/recipes',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'RECIPE:R' },
    children: [
      {
        path: '',
        name: 'recipes',
        component: () => import('@/views/Recipes.vue')
      },
      {
        path: ':id',
        name: 'recipe-details',
        component: () => import('@/views/RecipeDetails.vue')
      }
    ]
  },
  {
    path: '/orders',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PROD:R' },
    children: [
      {
        path: '',
        name: 'orders',
        component: () => import('@/views/ProductionOrders.vue')
      },
      {
        path: ':id',
        name: 'order-details',
        component: () => import('@/views/ProductionOrderDetails.vue')
      }
    ]
  },
  {
    path: '/warehouses',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'WAREHOUSE:R' },
    children: [
      {
        path: '',
        name: 'warehouses',
        component: () => import('@/views/Warehouses.vue')
      },
      {
        path: ':id',
        name: 'warehouse-details',
        component: () => import('@/views/WarehouseDetails.vue')
      }
    ]
  },
  {
    path: '/suppliers',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'SUPPLIER:R' },
    children: [
      {
        path: '',
        name: 'suppliers',
        component: () => import('@/views/Suppliers.vue')
      },
      {
        path: ':id',
        name: 'supplier-details',
        component: () => import('@/views/SupplierDetails.vue')
      }
    ]
  },
  {
    path: '/customers',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'CUSTOMER:R' },
    children: [
      {
        path: '',
        name: 'customers',
        component: () => import('@/views/Customers.vue')
      },
      {
        path: ':id',
        name: 'customer-details',
        component: () => import('@/views/CustomerDetails.vue')
      }
    ]
  },
  {
    path: '/purchase-orders',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PURCHASE_ORDER:R' },
    children: [
      {
        path: '',
        name: 'purchase-orders',
        component: () => import('@/views/PurchaseOrders.vue')
      },
      {
        path: ':id',
        name: 'purchase-order-details',
        component: () => import('@/views/PurchaseOrderDetails.vue')
      }
    ]
  },
  {
    path: '/boxes',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'COMP:R' },
    children: [
      {
        path: '',
        name: 'boxes',
        component: () => import('@/views/Boxes.vue')
      }
    ]
  },
  {
    path: '/routes',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'ROUTE:R' },
    children: [
      {
        path: '',
        name: 'routes',
        component: () => import('@/views/Routes.vue')
      },
      {
        path: ':id',
        name: 'route-details',
        component: () => import('@/views/RouteDetails.vue')
      }
    ]
  },
  {
    path: '/station-types',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'STATION_TYPE:R' },
    children: [
      {
        path: '',
        name: 'station-types',
        component: () => import('@/views/StationTypes.vue')
      }
    ]
  },
  {
    path: '/stations',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'STATION:R' },
    children: [
      {
        path: '',
        name: 'stations',
        component: () => import('@/views/Stations.vue')
      },
      {
        path: ':id',
        name: 'station-details',
        component: () => import('@/views/StationDetails.vue')
      }
    ]
  },
  {
    path: '/floor-monitor',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'STATION:R' },
    children: [
      {
        path: '',
        name: 'floor-monitor',
        component: () => import('@/views/ProductionFloorMonitor.vue')
      }
    ]
  },
  {
    path: '/operator-panel',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'WIP:R' },
    children: [
      {
        path: '',
        name: 'operator-panel',
        component: () => import('@/views/OperatorPanel.vue')
      }
    ]
  },
  {
    path: '/wip-console',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PROD:R' },
    children: [
      { path: '', name: 'wip-console', component: () => import('@/views/WipConsole.vue') },
      { path: ':id', name: 'wip-progress', component: () => import('@/views/WipProgress.vue') }
    ]
  },
  {
    path: '/lines',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'LINE:R' },
    children: [
      {
        path: '',
        name: 'lines',
        component: () => import('@/views/Placeholder.vue'),
        meta: { titleKey: 'nav.lines' }
      }
    ]
  },
  {
    path: '/plans',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PROD:R' },
    children: [
      {
        path: '',
        name: 'plans',
        component: () => import('@/views/PlannedOrders.vue')
      },
      {
        path: ':id',
        name: 'planned-order-details',
        component: () => import('@/views/ProductionOrderDetails.vue')
      }
    ]
  },
  {
    path: '/materials',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'INVENTORY:R' },
    children: [
      {
        path: '',
        name: 'materials',
        component: () => import('@/views/Inventory.vue')
      }
    ]
  },
  {
    path: '/products',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PRODUCT:R' },
    children: [
      {
        path: '',
        name: 'products',
        component: () => import('@/views/Placeholder.vue'),
        meta: { titleKey: 'nav.products' }
      }
    ]
  },
  {
    path: '/checks',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'QUALITY:R' },
    children: [
      {
        path: '',
        name: 'checks',
        component: () => import('@/views/Placeholder.vue'),
        meta: { titleKey: 'nav.checks' }
      }
    ]
  },
  {
    path: '/defects',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'QUALITY:R' },
    children: [
      {
        path: '',
        name: 'defects',
        component: () => import('@/views/Defects.vue'),
        meta: { titleKey: 'nav.defects' }
      }
    ]
  },
  {
    path: '/reports',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PROD:R' },
    children: [
      {
        path: '',
        name: 'reports',
        component: () => import('@/views/Reports.vue'),
        meta: { titleKey: 'nav.reports' }
      }
    ]
  },
  {
    path: '/settings',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PROD:R' },
    children: [
      {
        path: '',
        name: 'settings',
        component: () => import('@/views/Placeholder.vue'),
        meta: { titleKey: 'nav.settings' }
      }
    ]
  },
  {
    path: '/organization',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'PROD:R' },
    children: [
      {
        path: '',
        name: 'organization',
        component: () => import('@/views/Organization.vue')
      }
    ]
  },
  {
    path: '/users',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'USER:MANAGE_ROLES' },
    children: [
      {
        path: '',
        name: 'users',
        component: () => import('@/views/Users.vue')
      }
    ]
  },
  {
    path: '/incidents',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true, requiredPermission: 'INCIDENT:R' },
    children: [
      {
        path: '',
        name: 'incidents',
        component: () => import('@/views/Incidents.vue')
      },
      {
        path: ':id',
        name: 'incident-details',
        component: () => import('@/views/IncidentDetails.vue')
      }
    ]
  },

  // Catch-all redirect
  {
    path: '/:pathMatch(.*)*',
    redirect: '/'
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' };
    }
    return { top: 0 };
  }
});

// Navigation guard
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  // Check user authentication status. isLoading only covers the very first
  // navigation after app boot - re-check on every later navigation too when
  // the stored access token has since expired, otherwise a nav (as opposed
  // to an API call, which goes through the axios 401/signinSilent flow
  // instead) hits the expired-user branch below and hard-redirects to login
  // without ever attempting a silent/refresh-token renewal first.
  if (authStore.isLoading || authStore.user?.expired) {
    try {
      const user = await userManager.getUser();
      const resolvedUser = user && !user.expired ? user : await userManager.signinSilent();
      if (resolvedUser) authStore.setUser(resolvedUser);
      else authStore.clearUser();
    } catch (error) {
      authStore.clearUser();
    }
  }

  const isAuthenticated = authStore.isAuthenticated;
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const isPublic = to.matched.some((record) => record.meta.public);

  // If route requires auth and user is not authenticated
  if (requiresAuth && !isAuthenticated) {
    // Redirect to login
    userManager.signinRedirect({ state: { returnUrl: to.fullPath } });
    return;
  }

  // If user is authenticated and tries to access landing page
  if (isAuthenticated && to.name === 'landing') {
    const permissionsStore = usePermissionsStore();
    if (!permissionsStore.loaded) {
      await permissionsStore.fetch();
    }
    next(getDefaultLandingRoute(permissionsStore));
    return;
  }

  if (requiresAuth && isAuthenticated) {
    const permissionsStore = usePermissionsStore();
    if (!permissionsStore.loaded) {
      await permissionsStore.fetch();
    }

    const requiredPermission = to.matched
      .map((record) => record.meta.requiredPermission)
      .find((permission) => !!permission);

    if (requiredPermission && !permissionsStore.can(requiredPermission)) {
      useSnackbarStore().showError(i18n.global.t('general.accessDenied'));
      next(getDefaultLandingRoute(permissionsStore));
      return;
    }
  }

  next();
});

export default router;
export { userManager };
